import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Field } from '../components/ui.jsx';
import { api, ApiError } from '../api/client.js';
import { useAuth, homeFor } from '../state/AuthContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';

/**
 * Where a tenant lands when their landlord created the account for them. Until they
 * pick their own password the API refuses everything else, so this is not a screen
 * they can navigate away from.
 */
const SetPassword = () => {
  useTitle('Choose your password');
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({ newPassword: '', confirm: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();

    if (form.newPassword !== form.confirm) {
      setErrors({ confirm: 'The two passwords do not match.' });
      return;
    }

    setBusy(true);
    setErrors({});
    try {
      const result = await api.post('/auth/set-password', { newPassword: form.newPassword });
      updateUser(result.user);
      toast.success('Password set. Welcome.');
      navigate(homeFor(result.user), { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else setErrors({ _: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>Choose your own password</h1>
        <p className="muted">
          Your landlord set this account up for you with a temporary password. Pick your own now: the temporary one stops
          working, so nobody else can sign in as you.
        </p>

        {errors._ ? <div className="notice notice-bad">{errors._}</div> : null}

        <form className="stack" onSubmit={submit}>
          <Field label="Your new password" hint="At least 8 characters." error={errors.newPassword} required>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              autoFocus
              value={form.newPassword}
              onChange={(event) => setForm({ ...form, newPassword: event.target.value })}
            />
          </Field>

          <Field label="Type it again" error={errors.confirm} required>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={form.confirm}
              onChange={(event) => setForm({ ...form, confirm: event.target.value })}
            />
          </Field>

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Saving...' : 'Save my password'}
          </button>
        </form>
      </div>

      <aside className="auth-aside">
        <h2>What happens next</h2>
        <ul className="tick-list">
          <li>Your tenancy agreement, ready to read and sign</li>
          <li>Your rent statement: what is due and when</li>
          <li>Every receipt, kept for you and printable at any time</li>
          <li>Pay your rent by MTN or Orange money from your phone</li>
          <li>A direct line to your landlord through Messages</li>
        </ul>
        <p className="muted">
          You are signed in as {user?.email}. Nothing else on the platform opens up until this is done.
        </p>
      </aside>
    </div>
  );
};

export default SetPassword;
